import Region from "../Region";
import RegionArea from "../RegionArea";
import Phaser from 'phaser';

import * as ABCya from '@abcya/abcya_phaser';
import UIAssetKeys from '../UIAssetKeys';
import { AREAS, REGIONS, SHAPES } from '../gameData';

export default class TreeAndBenchArea extends RegionArea
{
    /**
     * Create the tree and bench area.
     * @param {Region} region the owner region
     */
    constructor(parentRegion)
    {
        super(parentRegion, AREAS[REGIONS.DOWNTOWN].TREE_AND_BENCH);

        this._createArea();

        this.reset();
    }

    /**
     * Reset should be called when you want to set the state to a disabled/inactive state.
     */
    reset()
    {
        super.reset();

        this.benchSeat.setAlpha(0);
        this.benchBack.setAlpha(0);
        this.trashcan.setAlpha(0);
        this.treeGrate.setAlpha(0);
        this.tree.setAlpha(0);
    }

    /**
     * Prepare should be called before you want to prepare the state to become active.
     */
    prepare()
    {
        super.prepare();

        this.benchSeat.disableInteractive();
        this.benchBack.disableInteractive();
        this.trashcan.disableInteractive();
        this.treeGrate.disableInteractive();
        this.tree.disableInteractive();
    }

    /**
     * Send all game objects to their start positions.
     */
    _sendObjectsToStartPositions()
    {
        super._sendObjectsToStartPositions();

        this.benchSeat.setAlpha(0.000001);
        this.benchBack.setAlpha(0.000001);
        this.trashcan.setAlpha(0.000001);
        this.treeGrate.setAlpha(0.000001);
        this.tree.setAlpha(0.000001);
    }

    /**
     * Creates the area, by adding the overlays to the parent region and creates the area information sidebar.
     */
    _createArea()
    {
        let benchSeatHitArea = new Phaser.Geom.Polygon(
            [
                { x: 6, y: 107 },
                { x: 183, y: 4 },
                { x: 242, y: 38 },
                { x: 64, y: 141 },
            ]);
        this.benchSeat = ABCya.Images.addImage(779, 582, UIAssetKeys._08_Downtown_Zoom_In_Tree_and_Bench_Slct_BnchSt, this.parentRegion).
            setInteractive(benchSeatHitArea, Phaser.Geom.Polygon.Contains);
        this.benchSeat.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () =>
        {
            this._onOverlayPointerDown(this.benchSeat, SHAPES.RECTANGLE);
        });

        let benchBackHitArea = new Phaser.Geom.Polygon(
            [
                { x: 3, y: 107 },
                { x: 183, y: 2 },
                { x: 204, y: 61 },
                { x: 23, y: 166 },
            ]);
        this.benchBack = ABCya.Images.addImage(735, 530, UIAssetKeys._08_Downtown_Zoom_In_Tree_and_Bench_Slct_BnchBk, this.parentRegion).
            setInteractive(benchBackHitArea, Phaser.Geom.Polygon.Contains);
        this.benchBack.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () =>
        {
            this._onOverlayPointerDown(this.benchBack, SHAPES.RECTANGLE);
        });

        let trashcanHitArea = new Phaser.Geom.Polygon(
            [
                { x: 46, y: 134 },
                { x: 1, y: 113 },
                { x: 1, y: 23 },
                { x: 46, y: 0 },
                { x: 91, y: 23 },
                { x: 91, y: 113 },
            ]);
        this.trashcan = ABCya.Images.addImage(887, 478, UIAssetKeys._08_Downtown_Zoom_In_Tree_and_Bench_Slct_TrshCan, this.parentRegion).setInteractive(trashcanHitArea, Phaser.Geom.Polygon.Contains);
        this.trashcan.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () =>
        {
            this._onOverlayPointerDown(this.trashcan, SHAPES.CYLINDER);
        });

        let treeGrateHitArea = new Phaser.Geom.Polygon(
            [
                { x: 98, y: 117 },
                { x: 0, y: 59 },
                { x: 98, y: 0 },
                { x: 196, y: 59 },
            ]);
        this.treeGrate = ABCya.Images.addImage(621, 457, UIAssetKeys._08_Downtown_Zoom_In_Tree_and_Bench_Slct_Grate, this.parentRegion).setInteractive(treeGrateHitArea, Phaser.Geom.Polygon.Contains);
        this.treeGrate.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () =>
        {
            this._onOverlayPointerDown(this.treeGrate, SHAPES.SQUARE);
        });

        this.tree = ABCya.Images.addImage(615, 202, UIAssetKeys._08_Downtown_Zoom_In_Tree_and_Bench_Slct_Tre, this.parentRegion);
        this.tree.setInteractive(new Phaser.Geom.Circle(this.tree.width / 2, this.tree.height / 2, this.tree.width / 2), Phaser.Geom.Circle.Contains);
        this.tree.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () =>
        {
            this._onOverlayPointerDown(this.tree, SHAPES.SPHERE);
        });

        this.shapeOverlays[SHAPES.RECTANGLE] = [this.benchBack, this.benchSeat];
        this.shapeOverlays[SHAPES.SQUARE] = [this.treeGrate];
        this.shapeOverlays[SHAPES.CYLINDER] = [this.trashcan];
        this.shapeOverlays[SHAPES.SPHERE] = [this.tree];

        this._createAreaInformation(0x5f5fd6, 0x2b4270);
    }

    /**
     * Zoom into the area.
     */
    zoomIn()
    {
        let tweens = super.zoomIn();

        tweens.push(
            {
                targets: [
                    this.parentRegion.blueBuilding, this.parentRegion.greenBuilding, this.parentRegion.peachBuilding
                ],
                duration: 500,
                props: { alpha: 0 },
                ease: Phaser.Math.Easing.Quadratic.Out,
            },
            { targets: this.parentRegion.treeAndBenchContainer, duration: 1000, props: { scale: 2.51923, x: -448, y: -712 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 0 },
            { targets: this.areaInformation, duration: 500, props: { x: this.areaInformation.onScreenPosition.x }, ease: Phaser.Math.Easing.Back.Out, offset: 500 }
        );

        return tweens;
    }

    /**
     * Zoom out of the area.
     */
    zoomOut()
    {
        let tweens = super.zoomOut();

        this.benchSeat.disableInteractive();
        this.benchBack.disableInteractive();
        this.trashcan.disableInteractive();
        this.treeGrate.disableInteractive();
        this.tree.disableInteractive();

        for (let shape in this.shapeOverlays)
        {
            // fade off the overlay
            tweens.push(
                {
                    targets: this.shapeOverlays[shape],
                    duration: 500,
                    props: { alpha: 0 },
                    ease: Phaser.Math.Easing.Quadratic.Out,
                    offset: 0
                }
            );
        }

        tweens.push(
            { targets: this.areaInformation, duration: 500, props: { x: this.areaInformation.offScreenPosition.x }, ease: Phaser.Math.Easing.Back.In, offset: 0 },
            { targets: this.parentRegion.treeAndBenchContainer, duration: 1000, props: { scale: 1, x: 0, y: 0 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 250 },
            { targets: [this.parentRegion.blueBuilding, this.parentRegion.greenBuilding, this.parentRegion.peachBuilding], duration: 500, props: { alpha: 1 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 500 },
        );

        return tweens;
    }
}